// Copy Button and Copy Snippet: copy a value to the clipboard, then show success or error.
import { I80Element, define, h, lateLabel } from "../base.js";
import { icon } from "../icons.js";

const ICONS = { idle: "clipboard-copy", success: "clipboard-checked", error: "clipboard-x" };
const RESET_DELAY = 1500;

/** What a copy button copies: text-to-copy, or the text of the element target-to-copy points at. */
function textToCopy(host) {
  const text = host.getAttribute("text-to-copy");
  if (text !== null) return text;
  const selector = host.getAttribute("target-to-copy");
  if (!selector) return "";
  const target = document.querySelector(selector);
  if (!target) {
    console.warn(`<${host.localName}> target-to-copy="${selector}" matches no element.`);
    return "";
  }
  if (target.matches("input, textarea, select")) return target.value;
  // Screen-reader-only text is not part of what the user sees, so it is not copied either.
  const srOnly = [...target.querySelectorAll(".sr-only")];
  for (const el of srOnly) el.style.setProperty("display", "none");
  const visible = target.innerText;
  for (const el of srOnly) el.style.removeProperty("display");
  return visible;
}

/**
 * The Clipboard API, with the old selection trick as a fallback: navigator.clipboard
 * exists only in a secure context, and plenty of server-rendered apps are served over
 * plain http on an intranet.
 */
async function write(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const area = h("textarea", { "aria-hidden": "true", style: "position:fixed;top:-1000px;opacity:0" });
      area.value = text;
      document.body.append(area);
      area.select();
      const ok = document.execCommand("copy");
      area.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

/** Copy, show the result for a moment, and tell the page what happened. */
async function copyFrom(host, text) {
  const success = await write(text);
  host._status = success ? "success" : "error";
  host.update();
  host.dispatchEvent(new CustomEvent("i80-copy", { bubbles: true, detail: { text, success } }));
  clearTimeout(host._timer);
  host._timer = setTimeout(() => {
    host._status = "idle";
    if (host.isConnected) host.update();
  }, RESET_DELAY);
  return success;
}

/** The message the live region announces, empty while idle. */
function statusMessage(host) {
  if (host._status === "success") return host.getAttribute("copied-message") ?? "Copied to clipboard";
  if (host._status === "error") return host.getAttribute("error-message") ?? "Failed to copy to clipboard";
  return "";
}

function liveRegion(host) {
  let live = host.part("live");
  if (!live) {
    live = h("span", { class: "sr-only", "aria-live": "polite", "data-i80-part": "live" });
    host.append(live);
  }
  return live;
}

// --- Copy Button ---------------------------------------------------------

/**
 * <i80-copy-button target-to-copy="#token">Copy</i80-copy-button>
 * <i80-copy-button text-to-copy="someSecret" icon-only>Copy your secret key</i80-copy-button>
 *
 * A secondary button with a clipboard icon; the icon turns into a check or a cross for a
 * moment after a click. Fires i80-copy with {text, success}.
 */
export class I80CopyButton extends I80Element {
  static get observedAttributes() {
    return ["text", "size", "icon-only", "full-width", "disabled", "text-to-copy", "target-to-copy", "copied-message", "error-message"];
  }

  setup() {
    this._status = "idle";
    let control = this.part("control") ?? this.child("button");
    if (!control) {
      control = h("button", { type: "button" });
      control.append(...this.takeContent());
      this.append(control);
    }
    control.setAttribute("data-i80-part", "control");
    const existing = control.querySelector(":scope > .i80-button__text");
    this._label = this.getAttribute("text") ?? (existing ?? control).textContent.trim();
    // Classes that are not ours belong to whoever wrote the button — a server-rendered
    // form, or <i80-code-block>, which positions its copy button with one. update() sets
    // the class attribute wholesale, so they are kept here and put back every time.
    this._extraClasses = [...control.classList].filter((c) => !/^i80-(button|copy-button)(--|$)/.test(c));
    this._control = control;
    this._live = liveRegion(this);
    control.addEventListener("click", () => this.copy());
  }

  adopt() {
    const label = lateLabel(this);
    if (label) this._label = label;
  }

  update() {
    const c = this._control;
    const size = this.choice("size", ["small", "medium"], "medium");
    const iconOnly = this.flag("icon-only");
    const label = this.getAttribute("text") ?? this._label;
    c.className = [
      "i80-button",
      "i80-button--color-secondary",
      this.flag("full-width") && "i80-button--width-full",
      iconOnly && "i80-button--is-icon-only",
      `i80-button--size-${size}`,
      "i80-copy-button",
      `i80-copy-button--status-${this._status}`,
      ...this._extraClasses,
    ].filter(Boolean).join(" ");
    c.disabled = this.flag("disabled");
    const iconEl = h("span", { class: "i80-button__icon" }, icon(ICONS[this._status], { stretched: true }));
    if (iconOnly) {
      c.replaceChildren(iconEl);
      c.setAttribute("aria-label", label);
    } else {
      c.removeAttribute("aria-label");
      // The clipboard icon always follows the text, as in Helios.
      c.replaceChildren(h("span", { class: "i80-button__text" }, label), iconEl);
    }
    this._live.textContent = statusMessage(this);
  }

  /** Copy now, as a click would. Resolves to whether it worked. */
  copy() {
    return copyFrom(this, textToCopy(this));
  }

  get status() {
    return this._status;
  }
}

// --- Copy Snippet -------------------------------------------------------

/**
 * <i80-copy-snippet>e4rt-yg80-39kt</i80-copy-snippet>
 * <i80-copy-snippet text-to-copy="e4rt-yg80-39kt" color="secondary" truncated></i80-copy-snippet>
 *
 * The value in code type, and the whole thing is the button that copies it.
 * Fires i80-copy with {text, success}.
 */
export class I80CopySnippet extends I80Element {
  static get observedAttributes() {
    return ["text-to-copy", "color", "full-width", "truncated", "label", "copied-message", "error-message"];
  }

  setup() {
    this._status = "idle";
    let control = this.part("control");
    if (control) {
      this._value = control.querySelector(".i80-copy-snippet__text")?.textContent.trim() ?? "";
    } else {
      // The tag's own text is the value; the whole snippet becomes one button.
      this._value = lateLabel(this) ?? "";
      control = h("button", { type: "button", "data-i80-part": "control" });
      this.append(control);
    }
    this._control = control;
    this._live = liveRegion(this);
    control.addEventListener("click", () => this.copy());
  }

  adopt() {
    const value = lateLabel(this);
    if (value) this._value = value;
  }

  update() {
    const value = this.getAttribute("text-to-copy") ?? this._value;
    const c = this._control;
    c.className = [
      "i80-copy-snippet",
      `i80-copy-snippet--color-${this.choice("color", ["primary", "secondary"], "primary")}`,
      `i80-copy-snippet--status-${this._status}`,
      this.flag("truncated") && "i80-copy-snippet--is-truncated",
      this.flag("full-width") && "i80-copy-snippet--width-full",
    ].filter(Boolean).join(" ");
    c.setAttribute("aria-label", this.getAttribute("label") ?? `copy ${value}`);
    c.replaceChildren(
      h("span", { class: "i80-text i80-typography-code-100 i80-font-weight-regular i80-copy-snippet__text" }, value),
      icon(ICONS[this._status], { className: "i80-copy-snippet__icon" }),
    );
    this._live.textContent = statusMessage(this);
  }

  /** Copy now, as a click would. Resolves to whether it worked. */
  copy() {
    return copyFrom(this, this.getAttribute("text-to-copy") ?? this._value);
  }

  get status() {
    return this._status;
  }
}

define("i80-copy-button", I80CopyButton);
define("i80-copy-snippet", I80CopySnippet);
